import type { ProjectClaim } from '../../types'

/**
 * Which claim the drawer opens on when the URL names none (figure S1).
 *
 * The figure never shows the Claims lens without a claim open — the right-hand
 * drawer IS half the screen, and a lens that shows a list with an empty column
 * beside it answers the reader's question with a blank. So the page picks one:
 * the highest-scoring claim, because the strongest thing this invention asserts
 * is what an inventor came here to check, and a combination claim (which no
 * single document asserts) wins a tie because it is the one a document-shaped
 * view could not have shown them at all.
 *
 * `?focus=claim:N` always wins. This is only the default.
 *
 * Returns a 1-BASED index — the number the reader sees, the URL carries and the
 * crumb prints — or null when there is nothing to focus.
 */
export function defaultFocusIndex(claims: ProjectClaim[] | undefined): number | null {
  if (!claims || claims.length === 0) return null
  let best = 0
  for (let i = 1; i < claims.length; i += 1) {
    const a = claims[i]
    const b = claims[best]
    const scoreA = a.novelty_score ?? -1
    const scoreB = b.novelty_score ?? -1
    if (scoreA > scoreB) { best = i; continue }
    if (scoreA === scoreB && a.supporting_document_ids.length > 1 && b.supporting_document_ids.length <= 1) {
      best = i
    }
  }
  return best + 1
}

/** The claim `?focus=claim:N` names, or the default, as a 1-based index. */
export function resolveFocusIndex(
  claims: ProjectClaim[] | undefined,
  fromUrl: number | null,
): number | null {
  const rows = claims ?? []
  if (fromUrl != null && fromUrl >= 1 && fromUrl <= rows.length) return fromUrl
  return defaultFocusIndex(rows)
}
